import React, { useEffect, useMemo, useRef } from 'react';
import { useMount } from 'ahooks';
import html2canvas from 'html2canvas';
import { base64String2ObjectURL } from '@/utils';
import { storagePrefixV2 } from '@/constant';
import { ShowTitle } from '../show-title';
import { ActiveBgColor } from '../active-bg-color';
import {
  setCurrentStep,
  setFormValue,
  setMarkPainScreenshotUrl,
  setNextBtnConfig,
  setPrevBtnConfig,
  useAIDiagnoseStore,
} from '../../store/useAIDiagnoseStore';

import cx from './index.module.scss';

interface Props {}

export const PAIN_AREA_CONFIG: Record<
  AIDiagnose.MarkPainArea,
  { name: string; defaultPlainUrl: string; defaultUrl: string; selectUrl: string }
> = {
  leftUpperTrapezius: {
    name: '左上斜方肌',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/adqwwfafawf-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/sdfdsfwe.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/asdqdqw.png',
  },
  rightUpperTrapezius: {
    name: '右上斜方肌',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/dqwfwqdqw-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/dsfdsfweg.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/dsgsfdsf.png',
  },
  leftLowerTrapezius: {
    name: '左下斜方肌',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/sddsfaad-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/dqwfqjwkf.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/qwdqwdwqf.png',
  },
  rightLowerTrapezius: {
    name: '右下斜方肌',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/sadasgsfdqwf-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/wqfdwqjiufnqwf.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/sdfwegwe.png',
  },
  leftInfraspinatus: {
    name: '左冈下肌',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/asdasvc-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/asdasdqwf.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/wqdsqwfwq.png',
  },
  rightInfraspinatus: {
    name: '右冈下肌',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/sadqwfqwg-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/asdqkfqwe.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/kjfdds.png',
  },
  leftBack: {
    name: '左侧背部',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/sdfqwfqgq-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/qwdqwoqjas.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/dfsdf.png',
  },
  rightBack: {
    name: '右侧背部',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/wdqwgfqwf-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/qwdqwfkg.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/sdfsdfgweg.png',
  },
  leftWaist: {
    name: '左侧腰部',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/gfqewdqwd-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/qwfwqf.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/wqeqwrqwf.png',
  },
  rightWaist: {
    name: '右侧腰部',
    defaultPlainUrl: storagePrefixV2 + '/ai-consulation/model-wrapper-2/wdqwgfwqg-min.png',
    defaultUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/asdxasd.png',
    selectUrl: storagePrefixV2 + '/ai-consulation/pain-marker-2/dsfsdfwe.png',
  },
};

export const MarkPainArea: React.FC<Props> = () => {
  const { markPainArea } = useAIDiagnoseStore.use.formValue();
  const markPainScreenshotUrl = useAIDiagnoseStore.use.markPainScreenshotUrl();
  const painMarkerRef = useRef(null);

  const renderBgLevel = useMemo(() => {
    if (!markPainArea?.length) return null;

    // 渲染的背景图片的等级取决于选择的最大严重等级
    return Math.min(5, markPainArea?.length) as AIDiagnose.PainLevel;
  }, [markPainArea]);

  const saveScreenshoot = async () => {
    const canvas = await html2canvas(painMarkerRef.current, { useCORS: true });
    // 将画布转换为数据 URL
    const imgData = canvas.toDataURL('image/png', 1.0);
    setMarkPainScreenshotUrl(base64String2ObjectURL(imgData));
  };

  useMount(() => {
    if (markPainScreenshotUrl) {
      URL.revokeObjectURL(markPainScreenshotUrl);
      setMarkPainScreenshotUrl('');
    }
  });

  useEffect(() => {
    setPrevBtnConfig({
      show: true,
      action: () => {
        setCurrentStep(prev => ({ currentStep: prev - 1 }));
      },
    });

    setNextBtnConfig({
      disable: !markPainArea?.length,
      action: async () => {
        setNextBtnConfig({ loading: true });
        await saveScreenshoot();
        setNextBtnConfig({ loading: false });
        setCurrentStep(prev => ({ currentStep: prev + 1 }));
      },
    });
  }, [markPainArea]);

  return (
    <div className={cx('MarkPainArea')}>
      <ShowTitle title="请点击标注您的疼痛区域" subTitle="可以选多区域" />
      <div className={cx('content')}>
        <div className={cx('img-wrapper')} ref={painMarkerRef}>
          <img className={cx('main-body-bg')} src={storagePrefixV2 + '/ai-consulation/mark-pain-body-bg.png'} alt="" />
          <div className={cx('pain-area-wrapper')}>
            {Object.keys(PAIN_AREA_CONFIG).map(item => (
              <div
                className={cx('marker-item', item)}
                key={item}
                style={{
                  backgroundImage: `url(${
                    PAIN_AREA_CONFIG[item as AIDiagnose.MarkPainArea]?.[
                      markPainArea?.includes(item as AIDiagnose.MarkPainArea) ? 'selectUrl' : 'defaultUrl'
                    ]
                  })`,
                }}
              >
                <div
                  onClick={() => {
                    setFormValue({
                      markPainArea: markPainArea?.includes(item as AIDiagnose.MarkPainArea)
                        ? markPainArea?.filter(item2 => item2 !== item)
                        : [...(markPainArea || []), item as AIDiagnose.MarkPainArea],
                    });
                  }}
                  className={cx('inner-hot-zone')}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <ActiveBgColor level={renderBgLevel} imgTop={30} />
    </div>
  );
};
